/**
 * Feature flags for gradual rollout and B2B/B2C mode.
 *
 * To flip to B2B mode later:
 *   1. Change B2B_MULTI_SENIOR to true
 *   2. Commit + deploy
 *   3. Manage Seniors nav + route + multi-senior list will reappear automatically
 *
 * B2B_PARTNERS gates the /partners page and any partner-facing UI
 * (e.g. PartnerBanner). Hidden for the May 20 B2C launch; restore at
 * ~200 subscribers by flipping to true AND removing the /partners
 * line from public/_redirects.
 *
 * REMOTE_LOCK_ENABLED gates the dashboard "Remote lock" quick action and
 * its drawer. OFF because the feature was never wired: onLock/onUnlock only
 * console.log, and isLocked was hardcoded false, so the button reported
 * success without sending anything to the device — a caregiver with a lost
 * phone was told it was secured when it was not. The UI and its callbacks
 * are kept intact for the Wave 1.5 rebuild; flipping this to true brings the
 * card back, but do NOT flip it until the real command path exists on both
 * ends: the `remote-lock` Supabase edge function (blocked on the FCM server
 * key) AND an Android receiver — the app today has no FCM listener at all,
 * and its Firebase config listener reads only managed_mode_enabled and
 * call_screening_enabled.
 *
 * @see docs/plans/2026-04-12-b2c-auth-dashboard-redesign.md
 * @see TODO.md "Remote Lock & Unlock (Section 3)"
 */
export const FEATURES = {
  B2B_MULTI_SENIOR: false,
  B2B_PARTNERS: false,
  REMOTE_LOCK_ENABLED: false,
  /**
   * Fall detection — Wave 2, OFF at launch.
   *
   * Must stay in lock-step with the server's `FALL_DETECTION_ENABLED` env var,
   * which controls whether the routes are registered at all. With the server
   * flag off those endpoints 404, so rendering `<FallDetectionAlert>` would put
   * a dismiss button on screen whose acknowledge / false-alarm calls fail —
   * worse than not showing the alert, because it looks handled.
   *
   * ⚠️ Flipping this on requires the server flag AND JWT + ownership on all
   * four fall-detection routes first — see
   * docs/plans/2026-08-11-server-authz-audit.md.
   */
  FALL_DETECTION_ENABLED: false,
  /**
   * Geofencing / safe zone — CUT from launch scope, OFF.
   *
   * The Android release build already ships `GEOFENCING_ENABLED=false`, so the
   * phone never writes `devices/{id}/activeAlert` and the breach alert cannot
   * fire in practice. But `<GeofenceBreachAlert>` was mounted unconditionally,
   * so the string "has left the safe zone" shipped in the bundle and the
   * component was one Android flag away from rendering a cut feature.
   *
   * Geofencing is also one of the capabilities the March privacy policy was
   * replaced for describing — see the locked product facts in CLAUDE.md.
   *
   * As of 2026-08-16 this gates every geofence surface in the dashboard, not
   * just the breach alert:
   *   - GPSZoneDrawer — zone-alert checkbox, alert-radius selector, the radius
   *     ring on the map, the Save footer, and the `devices/{id}/geofence` read
   *   - GeofenceSettingsModal — withheld whole (every control in it is zone
   *     configuration, including its map)
   *   - GeofenceBreachAlert — still neither imported nor rendered in
   *     Dashboard/index.tsx; dropping the import is what keeps its "has left
   *     the safe zone" copy out of the bundle. Gating alone would not.
   * The controls were CUT from v1 but shipped anyway. They are hidden, not
   * deleted, so Wave 1.5 is a flag flip rather than a revert.
   *
   * ⚠️ Flip in lock-step with the Android release flag, never ahead of it. The
   * RTDB rules deployed 2026-08-16 also deny geofence writes, so the server
   * side has to move first.
   *
   * ⚠️ This does NOT gate GPS location. Last-known location is a declared,
   * shipping feature (single point, off by default) — LocationPanel (F03), the
   * map in GPSZoneDrawer, and the last-known-location readout are deliberately
   * NOT behind this flag and were left untouched.
   *
   * Separately: `LocationMapModal` (which owns the "Locate Now" button and the
   * `devices/{id}/commands/locateNow` write) has no mount site and is
   * tree-shaken out of the bundle. That predates this flag and is unrelated to
   * geofencing — do not "fix" it by mounting the modal without checking that
   * the Android side consumes the locateNow command.
   */
  GEOFENCING_ENABLED: false,
} as const;
